import classNames from "classnames";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components/RobotoTooltip";

import styles from "./TimeDisplayField.module.css";

// Delay before the invalid-input tooltip surfaces. Keeps the red text as
// instant feedback while the explanation lands a beat later, so a transient
// invalid state (user catching their own typo before reading) doesn't flash
// the tooltip.
const INVALID_TOOLTIP_DELAY_MS = 200;

interface TimeInputProps {
  // The formatted live current time. Used as the displayed value when the
  // input isn't focused and as the reset target on Escape / failed blur.
  formatted: string;
  // Parent decides validity. Return null on success (parsed, in bounds, seek
  // performed, or no-op match) or an explanatory hint string to surface as
  // the invalid-state tooltip.
  onSubmit: (raw: string) => string | null;
}

export function TimeInput({ formatted, onSubmit }: TimeInputProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = React.useState(formatted);
  const [isFocused, setIsFocused] = React.useState(false);
  const [invalidHint, setInvalidHint] = React.useState<string | null>(null);
  const [showInvalidTooltip, setShowInvalidTooltip] = React.useState(false);
  const draftAtFocusRef = React.useRef<string>(formatted);
  // Set when handleKeyDown finalizes the edit (Enter-after-commit or Escape)
  // and explicitly calls blur(). The synchronous handleBlur that follows must
  // skip its implicit-confirm path: on Enter the seek already happened, on
  // Escape the user wanted to cancel.
  const skipBlurCommitRef = React.useRef<boolean>(false);

  // Keep the displayed value in sync with the live current time, but only
  // when the input isn't being edited — otherwise a playhead tick would
  // overwrite the user's draft.
  React.useEffect(() => {
    if (isFocused) {
      return;
    }
    setDraft(formatted);
  }, [formatted, isFocused]);

  // Debounce the invalid tooltip behind INVALID_TOOLTIP_DELAY_MS. The red
  // styling flips instantly on `invalidHint`; the tooltip waits so a quick
  // self-correction doesn't surface the bubble.
  React.useEffect(() => {
    if (invalidHint === null) {
      setShowInvalidTooltip(false);
      return;
    }
    const handle = window.setTimeout(
      () => setShowInvalidTooltip(true),
      INVALID_TOOLTIP_DELAY_MS,
    );
    return () => window.clearTimeout(handle);
  }, [invalidHint]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const hint = onSubmit(draft);
      if (hint === null) {
        setInvalidHint(null);
        skipBlurCommitRef.current = true;
        inputRef.current?.blur();
      } else {
        setInvalidHint(hint);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setDraft(formatted);
      setInvalidHint(null);
      skipBlurCommitRef.current = true;
      inputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      setInvalidHint(null);
      return;
    }
    if (draft !== draftAtFocusRef.current) {
      // Best-effort commit; on failure, fall back to the live current value
      // (the syncing effect will overwrite the draft on next tick anyway).
      if (onSubmit(draft) !== null) {
        setDraft(formatted);
      }
    }
    setInvalidHint(null);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    draftAtFocusRef.current = draft;
    // Defensive: if a prior blur() call somehow didn't fire onBlur (e.g. the
    // input was already unfocused), the flag could leak across edit sessions.
    skipBlurCommitRef.current = false;
    event.currentTarget.select();
  };

  return (
    <RobotoTooltip
      open={showInvalidTooltip}
      title={invalidHint ?? ""}
      placement="top"
      // Controlled-only: hover/focus on a valid field must never surface
      // the bubble, and we drive open/close from invalidHint ourselves.
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        spellCheck={false}
        aria-label="Current time"
        className={classNames(styles.timeInput, {
          [styles.timeInputInvalid]: invalidHint !== null,
        })}
        style={{ width: `${formatted.length}ch` }}
        onChange={(event) => {
          setDraft(event.target.value);
          if (invalidHint !== null) {
            setInvalidHint(null);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </RobotoTooltip>
  );
}
